/**
 * Formats a number with commas for thousands separator
 * For numbers with 4+ digits before decimal point
 */
export function formatNumberWithCommas(value: string | number): string {
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.]/g, '')) : value;
    
    if (isNaN(num)) return String(value);
    
    // Check if number has 4+ digits before decimal
    const parts = num.toString().split('.');
    const integerPart = parts[0];
    
    if (integerPart.length >= 4) {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: parts[1]?.length || 0,
            maximumFractionDigits: parts[1]?.length || 2,
        }).format(num);
    }
    
    return String(value);
}

/**
 * Formats a currency value with commas
 */
export function formatCurrencyWithCommas(value: string | number): string {
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.]/g, '')) : value;
    
    if (isNaN(num)) return typeof value === 'string' ? value : `$${value}`;
    
    // Check if number is whole (no decimal part)
    const isWhole = num % 1 === 0;
    
    const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: isWhole ? 0 : 2,
        maximumFractionDigits: isWhole ? 0 : 2,
    }).format(num);
    
    return formatted;
}

/**
 * Formats visit copay amounts. `0` / `0.0` display as `$0`;
 * only null/undefined/NaN show as an em dash.
 */
export function formatCopayAmount(value: number | null | undefined): string {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return formatCurrencyWithCommas(value);
}

export function formatBenefitSummary(raw: string | null | undefined): string {
    if (!raw?.trim()) return '—';

    const inNetworkMatch = raw.match(/In[- ]?Network:\s*([^/]+)/i);
    let value = (inNetworkMatch ? inNetworkMatch[1] : raw).trim();
    value = value.replace(/^Out[- ]of[- ]Network:\s*/i, '').trim();

    if (!value || /^not covered$/i.test(value) || /^not applicable$/i.test(value)) {
        return '—';
    }

    const percentAfterDeductible = value.match(/^(\d+(?:\.\d+)?)%\s+after\s+deductible$/i);
    if (percentAfterDeductible) {
        return `${percentAfterDeductible[1]}%*`;
    }

    return value;
}

export function formatVisitCopay(
    numerical: number | null | undefined,
    benefitString?: string | null,
): string {
    if (numerical != null && !Number.isNaN(Number(numerical))) {
        return formatCurrencyWithCommas(numerical);
    }
    return formatBenefitSummary(benefitString);
}

